import { Telemetry } from '@ctw/shared/context/telemetry';
import { tw } from '@ctw/shared/utils/tailwind';
import { Theme as RadixUiTheme } from '@radix-ui/themes';
import { uniqueId } from 'lodash-es';
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import { type NavigateOptions, useLocation, useNavigate, useNavigation } from 'react-router';

export type RouterOptions = NavigateOptions;

export type ZusService = 'zap-iframe' | 'zap-sof' | 'zap-standalone';

export const ZapVersions = ['v1', 'v2'] as const;
export type ZapVersion = (typeof ZapVersions)[number];

export const isZapVersion = (ZuiVersion: string | null | undefined): ZuiVersion is ZapVersion =>
  ZapVersions.includes(ZuiVersion as never);

type ZuiPresence =
  | 'legacyMainNavigationBar'
  | 'mainNavigationBar'
  | 'subNavigationBar'
  | 'dockedDrawer'
  | 'loadingSpinner';

type ZuiPresences = {
  [key in ZuiPresence]: boolean;
};

type ZuiPresenceRegistrations = {
  [key in ZuiPresence]: Array<string>;
};

const defaultZuiPresences: ZuiPresences = {
  dockedDrawer: false,
  legacyMainNavigationBar: false,
  mainNavigationBar: false,
  subNavigationBar: false,
  loadingSpinner: false,
} as const;

const defaultZuiPresenceRegistrations: ZuiPresenceRegistrations = {
  dockedDrawer: [],
  legacyMainNavigationBar: [],
  mainNavigationBar: [],
  subNavigationBar: [],
  loadingSpinner: [],
} as const;

// Presence state lives at module scope so `usePresence` can subscribe directly via
// `useSyncExternalStore` without going through React context, which means a change to
// a single presence key only re-renders subscribers of that hook, not every `useZuiContext()`
// consumer.
const presenceState: ZuiPresences = { ...defaultZuiPresences };
const presenceRegistrations: ZuiPresenceRegistrations = { ...defaultZuiPresenceRegistrations };
const presenceEventTarget = new EventTarget();
const PRESENCE_EVENT = 'change';

const registerPresence = (presence: ZuiPresence): (() => void) => {
  const presenceId = uniqueId();
  presenceRegistrations[presence].push(presenceId);

  if (presenceRegistrations[presence].length === 1) {
    presenceState[presence] = true;
    presenceEventTarget.dispatchEvent(new Event(PRESENCE_EVENT));
  }

  return () => {
    const index = presenceRegistrations[presence].indexOf(presenceId);
    if (index !== -1) {
      presenceRegistrations[presence].splice(index, 1);
    }

    if (presenceRegistrations[presence].length === 0) {
      presenceState[presence] = false;
      presenceEventTarget.dispatchEvent(new Event(PRESENCE_EVENT));
    }
  };
};

const subscribeToPresenceChanges = (callback: () => void) => {
  presenceEventTarget.addEventListener(PRESENCE_EVENT, callback);
  return () => presenceEventTarget.removeEventListener(PRESENCE_EVENT, callback);
};

interface ZuiState {
  service: string;
  navigate: (path: string, routerOptions?: RouterOptions) => void;
  isNavigating: boolean;
  pathname: string;
}

export const ZuiContext = createContext<ZuiState | null>(null);

export const useZuiContext = () => {
  const context = useContext(ZuiContext);

  if (!context) {
    throw new Error('useZuiContext must be used within a ZuiProvider');
  }

  return context;
};

/**
 * Register that a presence is on-screen until the comsuming component is
 * unmounted, or when `enabled` is set to `false`.
 */
export const useRegisterPresence = (presence: ZuiPresence, enabled = true): void => {
  useLayoutEffect(() => {
    if (!enabled) {
      return undefined;
    }
    return registerPresence(presence);
  }, [presence, enabled]);
};

export const usePresence = (presence: ZuiPresence): boolean => {
  const getSnapshot = useCallback(() => presenceState[presence], [presence]);
  return useSyncExternalStore(subscribeToPresenceChanges, getSnapshot, () => false);
};

// Only used to parse root-relative paths into a pathname and search params.
const URL_PARSING_BASE = 'http://localhost';

/**
 * Resolves `href` to `pathname + search`, carrying each of `persistentParams` over from
 * `currentSearch` unless `href` sets it. The hash is dropped. Throws if `href` is not a valid URL.
 */
export const withPersistentQueryParams = (
  href: string,
  currentSearch: string,
  persistentParams: Array<string> = [],
): string => {
  const requestedUrl = new URL(href, URL_PARSING_BASE);
  const currentSearchParams = new URLSearchParams(currentSearch);
  const destinationUrl = new URL(requestedUrl.pathname, URL_PARSING_BASE);

  for (const param of persistentParams) {
    const value = currentSearchParams.get(param);
    if (value) {
      destinationUrl.searchParams.set(param, value);
    }
  }

  for (const [param, value] of requestedUrl.searchParams.entries()) {
    destinationUrl.searchParams.set(param, value);
  }

  return `${destinationUrl.pathname}${destinationUrl.search}`;
};

interface ZuiProviderProps extends PropsWithChildren {
  service: ZusService | 'storybook' | 'playwright';
  navigatePersistentQueryParams?: Array<string>;
}

export const ZuiProvider = ({
  service,
  navigatePersistentQueryParams,
  children,
}: ZuiProviderProps) => {
  const routerNavigate = useNavigate();
  const navigation = useNavigation();
  const { pathname, search } = useLocation();
  const renderedLocationRef = useRef({ pathname, search });
  renderedLocationRef.current = { pathname, search };
  // Destination of the latest `navigate()` until the router settles it.
  const inFlightLocationRef = useRef<{ pathname: string; search: string } | null>(null);
  const pendingTelemetryPathnameRef = useRef<string | null>(null);
  const isNavigating = navigation.state !== 'idle';

  useEffect(() => {
    const pendingPathname = pendingTelemetryPathnameRef.current;
    pendingTelemetryPathnameRef.current = null;

    if (pendingPathname === pathname) {
      Telemetry.afterNavigate();
    }
  }, [pathname]);

  const zuiContext: ZuiState = useMemo(
    () => ({
      service,
      // Renders synchronously, so do not call it inside an effect.
      navigate: (requestedPath, routerOptions) => {
        const { pathname: pathnameBeforeNavigation, search: searchStringBeforeNavigation } =
          inFlightLocationRef.current ?? renderedLocationRef.current;

        let destinationPath: string;
        try {
          destinationPath = withPersistentQueryParams(
            requestedPath,
            searchStringBeforeNavigation,
            navigatePersistentQueryParams,
          );
        } catch {
          // Failure to parse `requestedPath` means the path was invalid, so we can't navigate to it
          return;
        }

        // Disallow navigating to the current URL (same pathname and search params, includes the `?` prefix).
        if (destinationPath === `${pathnameBeforeNavigation}${searchStringBeforeNavigation}`) {
          return;
        }

        const destinationUrl = new URL(destinationPath, URL_PARSING_BASE);
        const destinationLocation = {
          pathname: destinationUrl.pathname,
          search: destinationUrl.search,
        };

        Telemetry.beforeNavigate();
        // Search-only changes do not complete with `afterNavigate`.
        pendingTelemetryPathnameRef.current =
          destinationLocation.pathname === pathnameBeforeNavigation
            ? null
            : destinationLocation.pathname;

        inFlightLocationRef.current = destinationLocation;
        void Promise.resolve(
          routerNavigate(destinationPath, { flushSync: true, ...routerOptions }),
        ).finally(() => {
          if (inFlightLocationRef.current === destinationLocation) {
            inFlightLocationRef.current = null;
          }
        });
      },
      isNavigating,
      pathname,
    }),
    [service, isNavigating, pathname, navigatePersistentQueryParams, routerNavigate],
  );

  return (
    // https://www.radix-ui.com/themes/docs/components/theme
    <RadixUiTheme
      className={tw`flex h-[100vh] max-h-[100vh] w-[100vw] max-w-[100vw] flex-col overflow-hidden`}
      hasBackground={false}
      appearance="light"
      panelBackground="translucent"
      radius="medium"
      scaling="100%"
      accentColor="green"
      grayColor="gray"
    >
      <ZuiContext.Provider value={zuiContext}>{children}</ZuiContext.Provider>
    </RadixUiTheme>
  );
};
